import { cloneElement, useCallback, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Floating, useDismiss, type Placement } from './floating';

export interface PopoverProps {
  /** A single focusable element (usually a Button); it receives aria-expanded and the click handler. */
  trigger: ReactElement<Record<string, unknown>>;
  children: ReactNode | ((close: () => void) => ReactNode);
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Accessible name for the panel. */
  label: string;
  className?: string;
  matchWidth?: boolean;
}

/** Click-to-open floating panel for filters, quick forms and rich previews. Escape and outside click close it. */
export function Popover({ trigger, children, placement = 'bottom-start', open: controlled, onOpenChange, label, className, matchWidth }: PopoverProps) {
  const [internal, setInternal] = useState(false);
  const open = controlled ?? internal;
  const setOpen = useCallback(
    (next: boolean) => {
      setInternal(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const anchorRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => {
    setOpen(false);
    anchorRef.current?.focus();
  }, [setOpen]);
  const refs = useMemo(() => [anchorRef, panelRef], []);
  useDismiss(open, refs, close);

  const originalOnClick = trigger.props.onClick as ((e: unknown) => void) | undefined;
  const element = cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
    },
    'aria-haspopup': 'dialog',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
    onClick: (event: unknown) => {
      originalOnClick?.(event);
      setOpen(!open);
    },
  });

  return (
    <>
      {element}
      {open && (
        <Floating
          anchorRef={anchorRef}
          ref={panelRef}
          id={id}
          role="dialog"
          aria-label={label}
          placement={placement}
          matchWidth={matchWidth}
          className={cn('glass-strong max-w-[calc(100vw-1rem)] animate-scale-in rounded-xl p-3 text-sm shadow-pop ring-1 ring-line', className)}
        >
          {typeof children === 'function' ? children(close) : children}
        </Floating>
      )}
    </>
  );
}
